import React, { useEffect, useState } from "react";
import { GeocodingService, GeocodingResult } from "../../services/GeocodingService";

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 3;

type SearchStatus = "idle" | "loading" | "empty" | "error";

interface LocationSearchBarProps {
  onLocationSelected: (result: GeocodingResult) => void;
}

const LocationSearchBar = ({ onLocationSelected }: LocationSearchBarProps) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setStatus("idle");
      setHighlightedIndex(-1);
      return;
    }

    let cancelled = false;
    setStatus("loading");

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await GeocodingService.search(query.trim());
        if (cancelled) return;
        setSuggestions(results);
        setStatus(results.length === 0 ? "empty" : "idle");
        setHighlightedIndex(-1);
      } catch (error) {
        if (cancelled) return;
        console.error("Erreur lors de la recherche de lieu :", error);
        setSuggestions([]);
        setStatus("error");
        setHighlightedIndex(-1);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  const handleSelect = (result: GeocodingResult) => {
    onLocationSelected(result);
    setQuery(result.displayName);
    setSuggestions([]);
    setStatus("idle");
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      if (suggestions.length === 0) return;
      event.preventDefault();
      setHighlightedIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      if (suggestions.length === 0) return;
      event.preventDefault();
      setHighlightedIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter") {
      if (suggestions.length === 0) return;
      event.preventDefault();
      const selected = suggestions[highlightedIndex >= 0 ? highlightedIndex : 0];
      handleSelect(selected);
    } else if (event.key === "Escape") {
      setSuggestions([]);
      setStatus("idle");
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="location-search">
      <input
        type="text"
        className="location-search__input"
        placeholder="Rechercher un lieu…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        aria-label="Rechercher un lieu sur la carte"
        role="combobox"
        aria-expanded={suggestions.length > 0}
        aria-controls="location-search-listbox"
        autoComplete="off"
      />
      {status === "loading" && (
        <div className="location-search__message" aria-live="polite">Recherche…</div>
      )}
      {status === "empty" && (
        <div className="location-search__message">Aucun résultat</div>
      )}
      {status === "error" && (
        <div className="location-search__message location-search__message--error">
          Recherche indisponible
        </div>
      )}
      {suggestions.length > 0 && (
        <ul id="location-search-listbox" className="location-search__suggestions" role="listbox">
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.lat}-${suggestion.lon}`}
              role="option"
              aria-selected={index === highlightedIndex}
              className={`location-search__suggestion ${
                index === highlightedIndex ? "location-search__suggestion--highlighted" : ""
              }`}
              onMouseDown={() => handleSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.displayName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationSearchBar;
